import { useFormContext, useWatch } from 'react-hook-form';
import { useEffect } from 'react';
import {
  accountSearchColumns,
  hanThanhToanSearchColumns,
  khachHangSearchColumns,
  MA_CHUNG_TU,
  QUERY_KEYS
} from '@/constants';
import { AritoIcon, CurrencyInput, DocumentNumberField, FormField, SearchField } from '@/components/custom/erp';
import { AccountModel, HanThanhToan, KhachHang } from '@/types/schemas';
import { useDetailSelection } from '@/hooks/use-detail-selection';
import { FormFieldActions, FormFieldState } from '../../hooks';
import { Label } from '@/components/ui/label';
import { FormMode } from '@/types/form';

interface BasicInfoTabProps {
  formMode: FormMode;
  formState: {
    state: FormFieldState;
    actions: FormFieldActions;
  };
}

export default function BasicInfoTab({ formMode, formState: { state, actions } }: BasicInfoTabProps) {
  const { setValue } = useFormContext();
  const handleAccountSelection = useDetailSelection<AccountModel>({
    endpoint: QUERY_KEYS.TAI_KHOAN,
    onSelect: actions.setTaiKhoan
  });
  const ngay_ct = useWatch({ name: 'ngay_ct' });

  useEffect(() => {
    if (ngay_ct) {
      setValue('ngay_ct0', ngay_ct);
    }
  }, [ngay_ct, setValue]);
  return (
    <div className='flex flex-col gap-3 p-4'>
      {/* Customer Information Section */}
      <div className='grid grid-cols-12 gap-6'>
        {/* Left Column */}
        <div className='col-span-6 space-y-1'>
          <div className='flex items-center'>
            <Label className='w-36 font-semibold'>Loại hóa đơn</Label>
            <div className='flex-1'>
              <FormField
                name='ma_ngv'
                type='select'
                disabled={formMode === 'view'}
                options={[
                  { value: '1', label: '1. Giảm giá' },
                  { value: '2', label: '2. Tăng giá' }
                ]}
                className='w-40'
              />
            </div>
          </div>
          <div className='flex items-center'>
            <Label className='w-36 font-semibold'>Mã nhà cung cấp</Label>
            <SearchField<KhachHang>
              type='text'
              value={state.khachHang?.customer_code || ''}
              searchEndpoint={`/${QUERY_KEYS.KHACH_HANG}/`}
              searchColumns={khachHangSearchColumns}
              onRowSelection={actions.setKhachHang}
              columnDisplay='customer_code'
              dialogTitle='Danh mục nhà cung cấp'
              className='w-100'
              disabled={formMode === 'view'}
            />

            <div className='ml-6 flex items-center'>
              <Label className='w-20 font-semibold'>Mã số thuế</Label>
              <FormField
                name='ma_so_thue'
                type='text'
                disabled={formMode === 'view'}
                placeholder='Nhập và tra cứu'
                value={state.khachHang?.tax_code || ''}
              />
              <button className='ml-2 flex items-center justify-center border border-gray-200 px-3 py-1.5'>
                <AritoIcon icon={15} className='shrink-0' />
              </button>
              <button className='ml-2 flex items-center justify-center border border-gray-200 px-3 py-1.5'>
                <AritoIcon icon={888} />
              </button>
            </div>
          </div>

          <div className='flex items-center'>
            <Label className='w-36 font-semibold'>Tên nhà cung cấp</Label>
            <div className='flex-1'>
              <FormField
                name='ten_kh_thue'
                type='text'
                disabled={formMode === 'view'}
                value={state.khachHang?.customer_name || ''}
                placeholder='Nhập tên nhà cung cấp/đơn vị'
              />
            </div>
          </div>

          <div className='flex items-center'>
            <Label className='w-36 font-semibold'>Địa chỉ</Label>
            <div className='flex-1'>
              <FormField
                name='dia_chi'
                type='text'
                disabled={formMode === 'view'}
                labelClassName='w-48 font-semibold'
                value={state.khachHang?.address || ''}
                placeholder='Nhập địa chỉ nhà cung cấp'
              />
            </div>
          </div>

          <div className='flex items-center'>
            <Label className='w-36 font-semibold'>Tài khoản công nợ</Label>
            <SearchField<AccountModel>
              type='text'
              value={state.khachHang?.account_data?.code || state.taiKhoan?.code || ''}
              relatedFieldValue={state.khachHang?.account_data?.name || state.taiKhoan?.name || ''}
              searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
              columnDisplay='code'
              displayRelatedField='name'
              searchColumns={accountSearchColumns}
              dialogTitle='Danh sách tài khoản'
              onRowSelection={handleAccountSelection}
              disabled={formMode === 'view'}
              className='w-100'
            />
          </div>

          <div className='flex items-center'>
            <div className='flex-1'>
              <FormField
                label='Diễn giải'
                name='dien_giai'
                type='text'
                disabled={formMode === 'view'}
                labelClassName='w-36 font-semibold'
              />
            </div>
          </div>
        </div>

        {/* Middle Column */}
        <div className='col-span-3 space-y-1'>
          <div className='flex h-8 items-center'></div>

          <div className='flex items-center'>
            <FormField
              name='ong_ba'
              label='Người giao'
              labelClassName='w-32 font-semibold'
              inputClassName='w-6 text-red-600'
              disabled={formMode === 'view'}
              className='w-[250px]'
              value={state.khachHang?.contact_person || ''}
              placeholder='Nhập tên người giao'
            />
          </div>

          <div className='flex items-center'>
            <FormField
              name='e_mail'
              label='Email'
              labelClassName='w-32 font-semibold'
              inputClassName='w-64'
              className='w-[250px]'
              disabled={formMode === 'view'}
              value={state.khachHang?.email || ''}
              placeholder='Nhập email'
            />
          </div>
          <div className='flex items-center'>
            <Label className='mt-2 w-36 font-semibold'>Hạn thanh toán</Label>
            <SearchField<HanThanhToan>
              type='text'
              value={state.hanThanhToan?.ten_tt || state.khachHang?.payment_term_data?.ten_tt || ''}
              searchEndpoint={`/${QUERY_KEYS.HAN_THANH_TOAN}/`}
              columnDisplay='ten_tt'
              searchColumns={hanThanhToanSearchColumns}
              dialogTitle='Danh mục hạn thanh toán'
              className='w-100'
              onRowSelection={actions.setHanThanhToan}
              placeholder='Hạn thanh toán'
              disabled={formMode === 'view'}
            />
          </div>
        </div>

        {/* Right Column */}
        <div className='col-span-3 flex'>
          {/* Tab Content */}
          <div className='flex-1 pr-2'>
            <DocumentNumberField
              ma_ct={MA_CHUNG_TU.MUA_HANG.PHIEU_NHAP_DIEU_CHINH_GIA_HANG_MUA}
              quyenChungTu={state.quyenChungTu}
              onQuyenChungTuChange={actions.setQuyenChungTu}
              soChungTu={state.soChungTu}
              onSoChungTuChange={actions.setSoChungTu}
              disabled={formMode !== 'add'}
              classNameSearchField='w-full'
              labelClassName='font-semibold w-32'
              formMode={formMode}
            />

            <div className='flex items-center'>
              <Label className='w-32 font-semibold'>Ngày chứng từ</Label>
              <div className='flex-1'>
                <FormField name='ngay_ct' type='date' disabled={formMode === 'view'} />
              </div>
            </div>
            <div className='flex items-center'>
              <Label className='w-32 font-semibold'>Số hóa đơn</Label>
              <div className='flex-1'>
                <FormField name='so_ct0' placeholder='SỐ HÓA ĐƠN' type='text' disabled={formMode === 'view'} />
              </div>
            </div>
            <div className='flex items-center'>
              <Label className='w-32 font-semibold'>Ký hiệu</Label>
              <div className='flex-1'>
                <FormField name='so_ct2' placeholder='KÝ HIỆU' type='text' disabled={formMode === 'view'} />
              </div>
            </div>
            <div className='flex items-center'>
              <Label className='w-32 font-semibold'>Ngày hoá đơn</Label>
              <div className='flex-1'>
                <FormField name='ngay_ct0' type='date' disabled={formMode === 'view'} />
              </div>
            </div>

            <CurrencyInput formMode={formMode} labelClassName='w-32 font-semibold' classNameInput='w-full' />

            <div className='flex items-center'>
              <Label className='w-32 font-semibold'>Trạng thái</Label>
              <div className='flex-1'>
                <FormField
                  name='status'
                  type='select'
                  disabled={formMode === 'view'}
                  options={[
                    { value: '0', label: 'Chưa ghi sổ' },
                    { value: '3', label: 'Chờ duyệt' },
                    { value: '5', label: 'Đã ghi sổ' }
                  ]}
                />
              </div>
            </div>

            <div className='mt-3 flex'>
              <div className='mb-4 h-2 w-32 shrink-0' />
              <FormField
                label='Dữ liệu được nhận'
                name='transfer_yn'
                type='checkbox'
                disabled={true}
                labelClassName='w-36 font-semibold'
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
